/**
 * useQuotaData Hook
 *
 * Combines entitlements and quota hooks into a unified quota data interface.
 * Provides backward-compatible quotaData shape for child components.
 *
 * This hook consolidates:
 * - useEntitlements (static: tier, characterLimit, quotaPolicy)
 * - useQuota (dynamic: used, remaining, resetAt)
 * - QuotaData transformation logic
 *
 * Extracted from index.tsx to reduce complexity.
 */
import { useEntitlements } from "./useEntitlements";
import { useQuota } from "./useQuota";
import type { UniversalQuotaData, QuotaSource } from "./quota/quotaTypes";

interface UseQuotaDataResult {
  /** Combined quota data for child components */
  quotaData: UniversalQuotaData | null;
  /** User's subscription tier */
  tier: string | undefined;
  /** Maximum character limit for messages */
  characterLimit: number;
  /** Whether entitlements are loading */
  isEntitlementsLoading: boolean;
  /** Whether quota is loading */
  isQuotaLoading: boolean;
}

/**
 * Hook that combines entitlements and quota into a unified data shape.
 */
export function useQuotaData(): UseQuotaDataResult {
  // Entitlements (static: tier, characterLimit, quotaPolicy) - 5-min cache
  const {
    tier,
    characterLimit,
    quotaPolicy,
    isLoading: isEntitlementsLoading,
  } = useEntitlements();

  // Quota usage (dynamic: used, remaining, resetAt) - 30-sec cache + Realtime
  const {
    used,
    remaining,
    resetAt,
    usagePercent,
    formattedResetTime,
    isLoading: isQuotaLoading,
  } = useQuota(quotaPolicy);

  // Transform to backward-compatible quotaData shape
  const quotaData: UniversalQuotaData | null = quotaPolicy
    ? {
        used,
        limit: quotaPolicy.limit,
        remaining,
        resetAt,
        resetPeriod: quotaPolicy.window,
        source: mapScopeToSource(quotaPolicy.scope),
        usagePercent,
        formattedResetTime,
        tier,
      }
    : null;

  return {
    quotaData,
    tier,
    characterLimit,
    isEntitlementsLoading,
    isQuotaLoading,
  };
}

/**
 * Maps quota scope to source type for backward compatibility.
 */
function mapScopeToSource(scope: string): QuotaSource {
  switch (scope) {
    case "device":
      return "anonymous";
    case "family":
      return "family";
    default:
      return "individual";
  }
}

export default useQuotaData;
